import { facetKeys } from '@/features/jobs/utils/aggregations';
import type { AggregationValue, SearchInput } from '@/types/job.types';

/**
 * Query string for the requisition search, aggregation and count calls — mirrors Flutter
 * IWebHomeRepository.getQueryParams(): `skills` (split on commas, joined with `$:`) or `roles`,
 * `locations`, `hotJob=true`, then one `key=v1$:v2` pair per selected filter (skills prefixed
 * with `~`, the sort field by its key).
 *
 * Search-bar text is literal text, so every value is percent-encoded and the `$:` separators are
 * not. Flutter put the terms in raw: a lone `%` was an invalid escape, so Tomcat dropped the
 * whole parameter and a search for "%" returned every job (QA-001); `+` arrived as a space, `&`
 * split the value and `#` cut off the rest of the URL. Tomcat decodes once, so the backend still
 * receives the same text.
 *
 * `%` itself is left out of the search-bar text (searchText): the API answers any URL holding an
 * encoded percent sign (`%25`) with 401, which the error interceptor would turn into a sign-out,
 * and the search analyzer drops `%` anyway (its char filter keeps only letters, digits,
 * whitespace, `#` and `+`), so the results are the same. A search with nothing searchable left
 * is answered without a request (hasNoSearchableTerms).
 */
export function buildSearchQueryParams(searchInput?: SearchInput): string {
  if (!searchInput) return '';

  const parts: string[] = [];

  if (searchInput.skills && searchInput.switchIndex === 0) {
    const terms = searchInput.skills.split(',');
    // A term with nothing searchable can't match anything; leave it out unless all are like that
    const searchable = terms.filter(isSearchable);
    const encoded = (searchable.length ? searchable : terms).map((term) => encodeURIComponent(searchText(term)));
    parts.push(`skills=${encoded.join('$:')}`);
  } else if (searchInput.roles && searchInput.switchIndex === 1) {
    parts.push(`roles=${encodeURIComponent(searchText(searchInput.roles))}`);
  }

  if (searchInput.locations) {
    parts.push(`locations=${encodeURIComponent(searchText(searchInput.locations))}`);
  }

  if (searchInput.isHotJob) {
    parts.push('hotJob=true');
  }

  if (searchInput.filters && Object.keys(searchInput.filters).length > 0) {
    const filterParts: string[] = [];
    for (const [key, values] of Object.entries(searchInput.filters)) {
      if (key === 'sortBy' || !values.length) continue;

      const encoded = values.flatMap((v: AggregationValue) => {
        if (v.catgKey === 'selectedSortField') {
          return [encodeURIComponent(v.additionalFields?.key ?? '')];
        }
        const prefix = key === 'skills' ? '~' : '';
        // A merged facet value (e.g. "Engineering" / "engineering ") sends every raw key
        return facetKeys(v).map((k) => encodeURIComponent(`${prefix}${k}`));
      });

      filterParts.push(`${key}=${encoded.join('$:')}`);
    }
    if (filterParts.length) {
      parts.push(filterParts.join('&'));
    }
  }

  return parts.join('&');
}

/** Search-bar text as sent: `%` becomes a space, as the search analyzer treats it. */
export function searchText(value: string): string {
  return value.replace(/%/g, ' ');
}

/**
 * Characters a search term can match on. The analyzer keeps letters, digits, `#` and `+`
 * ("C#", "C++"); letters of other scripts count too, so only clear punctuation-only terms are
 * treated as unsearchable.
 */
const SEARCHABLE_CHARACTER = /[\p{L}\p{N}#+]/u;

function isSearchable(term: string): boolean {
  return SEARCHABLE_CHARACTER.test(searchText(term));
}

/**
 * True when the search bar holds terms none of which can match (e.g. "%", "_", "%%"). The
 * backend matches nothing for such a term, but sent as a blank value it would be ignored and
 * every job would come back, so the API module answers "no jobs" without a request.
 */
export function hasNoSearchableTerms(searchInput?: SearchInput): boolean {
  if (!searchInput) return false;
  if (searchInput.switchIndex === 0 && searchInput.skills) {
    if (!searchInput.skills.split(',').some(isSearchable)) return true;
  } else if (searchInput.switchIndex === 1 && searchInput.roles) {
    if (!isSearchable(searchInput.roles)) return true;
  }
  return !!searchInput.locations && !isSearchable(searchInput.locations);
}
